import { useContext, useEffect, useRef, useMemo, useState } from 'react'
import { useFormikContext, Field, ErrorMessage } from 'formik'
import { motion } from "framer-motion"
import classnames from 'classnames/bind'
import * as MotionElement from '~/components/ui/MotionElement'
import {
  FormContent, FormHeader, BookingSummary, ButtonDrawer, CarRadioField, FixedPriceSwitch, TripTypeSwitch,
  FormError, Notifications, ElectricCarCheckbox, SectionHeading
} from '~/components'
import { StepperContext } from '~/components/ui/ViewStepper'
import { StoreContext } from '~/store'
import { getCarTypes, getAvailableVehicleOptions, getVehicleOptionPrice, isEditMode } from '~/utils/data'
import EditFieldsExpirationWarning from '../EditFieldsExpirationWarning'
import { VehicleOptionsRenderer, useVehicleOptions } from './VehicleOptionsRenderer'
import PartnerSelection from '../PartnerSelection'
import * as common from '~/config/common.module.css'
import * as styles from './index.module.css'
import { useTranslation } from 'react-i18next'
import { track } from '~/utils/gtm'

const cx = classnames.bind(common)

const findPrice = (prices, serviceType, providerId = null) =>
  (providerId && prices.find(p => p.serviceType === serviceType && p.providerId === providerId))
  || prices.find(p => p.serviceType === serviceType)

const renderPrice = (prices, serviceType, providerId = null) => {
  const price = findPrice(prices, serviceType, providerId)
  if (price === undefined) {
    console.error('Could not find price for servicetype:', serviceType)
    return null
  }
  return `${price.finalPrice}:-`
}

const getOriginalPrice = (prices, serviceType, providerId = null) => {
  const price = findPrice(prices, serviceType, providerId)
  if (price === undefined || price.finalPrice === price.fullPrice) return null
  return `${price.fullPrice}:-`
}

const getReducedPrice = (prices, serviceType, providerId = null) => {
  const price = findPrice(prices, serviceType, providerId)
  if (price === undefined) {
    console.error('Could not find price for servicetype:', serviceType)
    return false
  }
  if (price.finalPrice === price.fullPrice) return false
  else {
    let type = false;
    price.discountComponents?.forEach((dis) => {
      if(dis.type == 'campaign') type = dis.name;
    })
    return type;
  }
}

const SelectHow = ({fullscreen, currentView}) => {
  const { t } = useTranslation()
  const carTypeRef = useRef()
  const partnerHeadingRef = useRef()
  const [partnerError, setPartnerError] = useState(false)
  const { prev, next } = useContext(StepperContext)
  const { user, setIsCompanyTrip } = useContext(StoreContext)

  const { values, setFieldValue, handleChange, handleBlur } = useFormikContext()
  // const showAvailability = values.departure?.address !== undefined && values.pickupTime === ''
  const inEditMode = isEditMode(values)
  const disableFixedPrice = values.destination?.address === undefined || values.autoValues.fixedPriceAvailable === null
  const fixedPrice = values.autoValues.fixedPrice !== null ? values.autoValues.fixedPrice : values.fixedPrice

  // Memoize vehicleOptions to prevent infinite render loop
  const vehicleOptions = useMemo(() => {
    return getAvailableVehicleOptions(values)
  }, [
    values.bookingId,
    values.editableOptions,
    values.availability,
    values.pickupTime,
    values.serviceType,
    values.autoValues.serviceType,
    values.selectedProviderId
  ])

  // In edit mode, determine which options can be edited and which are just shown (disabled)
  const editableVehicleOptions = useMemo(() => {
    if (!inEditMode) return vehicleOptions
    return values.editableOptions?.vehicleOptions?.map(opt => opt.vehicleOption) || []
  }, [inEditMode, values.editableOptions, vehicleOptions])

  // Use the configuration-based hook to process all vehicle options
  const processedOptions = useVehicleOptions({
    vehicleOptions,
    editableVehicleOptions,
    values,
    inEditMode,
  })

  // Extract specific options for backward compatibility and useEffect dependencies
  const petAvailable = processedOptions.pet?.available || false
  const petCurrentlySelected = processedOptions.pet?.currentlySelected || false
  const lowAvailable = processedOptions.low?.available || false
  const lowCurrentlySelected = processedOptions.low?.currentlySelected || false
  const boosterCurrentlySelected = processedOptions.boosterSeat?.currentlySelected || false
  const numSeatsMax = processedOptions.boosterSeat?.numSeatsMax || 0

  // Options not in config (handled separately)
  const electricCarAvailable = vehicleOptions.includes('electric')
  const stationWagonAvailable = false; //vehicleOptions.includes('stationWagon')
  const showCompany = user && user?.companyConnections?.length > 0

  // Memoize company to prevent unnecessary recalculations and useEffect triggers
  const company = useMemo(() => {
    return values.isCompanyTrip && user?.companyConnections?.length > 0 ? user.companyConnections[0] : false
  }, [values.isCompanyTrip, user?.companyConnections])

  // Multiple partners: servicesByProvider filtered to providers that support the selected service type
  // multipleProviders: providers filtered to those supporting the current service type.
  // Returns null only when there are globally ≤1 providers (hides the section entirely).
  // Returns a 1-item array when only 1 provider supports the current service type
  // (section stays visible to avoid UI jumps; that provider is auto-selected below).
  const multipleProviders = useMemo(() => {
    const key = values.pickupTime === '' ? 'direct' : 'prebook'
    let providers = values.availability?.[key]?.servicesByProvider
    if (!Array.isArray(providers) || providers.length === 0) return null

    // In edit mode, lock to the original booking's provider — partner cannot be changed.
    // Filter down to just that provider; if not found in current availability, keep all.
    if (inEditMode && values.lockedProviderId) {
      const locked = providers.find(p => p.providerData?.providerId === values.lockedProviderId)
      if (locked) providers = [locked]
    }

    // Hide the section only when the sole provider is TSAB (native Taxi Stockholm flow)
    if (providers.length === 1 && providers[0].providerData?.providerId === 'TSAB') return null
    const effectiveSt = values.serviceType === 'estate' ? 'taxi' : values.serviceType

    // Collect add-on keys the user has selected
    const selectedAddonKeys = []
    if (values.pet) selectedAddonKeys.push('pet')
    if (values.low) selectedAddonKeys.push('low')
    if (values.booster > 0) selectedAddonKeys.push('boosterSeat1')
    if (values.rearFacingChildSeat) selectedAddonKeys.push('rearFacingChildSeat')
    if (values.electric) selectedAddonKeys.push('electric')

    // null renders as Taxameter in FixedPriceSwitch (Boolean(null) === false), so treat it the same
    const requiredPriceType = fixedPrice ? 'FIXED' : 'TAXIMETER'

    return providers.filter(p => {
      const a = p.availabilities?.find(a => a.serviceType === effectiveSt)
      if (!a || a.availableAt === 'NOT_AVAILABLE') return false
      // Filter out providers that don't support all selected add-ons
      if (!selectedAddonKeys.every(k => a.vehicleOptions?.includes(k))) return false
      // Filter out providers that don't support the current price type
      if (requiredPriceType) {
        const priceTypes = p.priceAndPaymentRules?.priceTypes || []
        if (!priceTypes.some(pt => pt.type === requiredPriceType)) return false
      }
      return true
    })
  }, [values.availability, values.pickupTime, values.serviceType, values.pet, values.low, values.booster, values.rearFacingChildSeat, values.electric, fixedPrice, inEditMode, values.lockedProviderId])

  // Section shows whenever there are globally 2+ providers, regardless of current service type
  const shouldShowProviderSelection = multipleProviders !== null
  const showPrices = !inEditMode && fixedPrice && values.priceEstimate && !shouldShowProviderSelection
  const showDiscountPill = !inEditMode && fixedPrice && values.priceEstimate

  // Memoize partnerData to prevent unnecessary recalculations
  const partnerData = useMemo(() => {
    if (values?.availability?.direct?.providerData?.providerId && (values?.availability?.direct?.providerData?.providerId !== "TSAB" || CONFIG.svea_module)) {
      return values?.availability?.direct?.providerData
    }
    if (values?.availability?.prebook?.providerData?.providerId && (values?.availability?.prebook?.providerData?.providerId !== "TSAB" || CONFIG.svea_module)) {
      return values?.availability?.prebook?.providerData
    }
    return null
  }, [values.availability])

  // Check availability from priceAndPaymentRules
  const availabilityKey = values.pickupTime === '' ? 'direct' : 'prebook'
  const priceTypes = values.availability?.[availabilityKey]?.priceAndPaymentRules?.priceTypes || []
  const fixedPriceAvailable = priceTypes.find(item => item.type === 'FIXED') !== undefined
  const taxiMeterAvailable = priceTypes.find(item => item.type === 'TAXIMETER') !== undefined
  
  const fixedPriceNotAvailable = values.autoValues.fixedPriceAvailable === false || (partnerData && !CONFIG.svea_module) || !fixedPriceAvailable
  const taxameterNotAvailable = !taxiMeterAvailable

    console.log("partnerData", partnerData, fixedPrice);

  // Memoize fields to prevent infinite render loop
  const fields = useMemo(() => {
    return getCarTypes()
      .filter(field => field.value !== 'delivery')
      .filter(field => {
        const key = values.pickupTime === '' ? 'direct' : 'prebook'
        const name = field.asValue

        // Check if availability data exists for this key
        if (!values.availability?.[key]?.availabilities) {
          return false
        }

        const estateAvailability = values.availability[key].availabilities.find(item => 'taxi' === item.serviceType)
        if (field.value === 'estate' && !estateAvailability?.vehicleOptions?.includes('stationWagon')) return false;

        return values.availability[key].availabilities.find(item => name === item.serviceType) !== undefined
      })
      .map(field => {
        const key = values.pickupTime === '' ? 'direct' : 'prebook'
        const name = field.asValue
        if (values.availability[key]?.availabilities?.length) {
          field.availability = values.availability[key].availabilities.find(item => name === item.serviceType)
          field.disabled = field.availability.availableAt === 'NOT_AVAILABLE'
        }
        // In edit mode, disable service types that aren't currently selected
        if (inEditMode && field.value !== values.serviceType) {
          field.disabled = true
          field.hideDisabledPill = true // Don't show pill when disabled in edit mode
        }
        if (showPrices) field.price = renderPrice(values.priceEstimate.prices, field.asValue)
        if (showPrices) field.originalPrice = getOriginalPrice(values.priceEstimate.prices, field.asValue)
        if (showDiscountPill) field.reducedPrice = getReducedPrice(values.priceEstimate.prices, field.asValue)
        if (field.value === values.serviceType) field.labelRef = carTypeRef
        // Disable if passenger count exceeds this service type's capacity
        if (!field.disabled && values.passengerCount > 0 && field.availability?.maxTravelers > 0) {
          if (values.passengerCount > field.availability.maxTravelers) field.disabled = true
        }
        return field
      })
  }, [
    values.availability,
    values.pickupTime,
    values.serviceType,
    values.priceEstimate,
    values.passengerCount,
    inEditMode,
    showPrices,
    showDiscountPill
  ])
  

  const wrappedNext = () => {
    if (shouldShowProviderSelection && !values.selectedProviderId) {
      setPartnerError(true)
      partnerHeadingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }
    if (!user) {
      next()
    }
    else {
      track('initiateCheckout', {
        carType: values.serviceType,
        preBook: values.pickupTime ? true : false,
        userId: user?.userId || false,
      })
      next('confirm')
    }
  }

  useEffect(() => {
    if (values.selectedProviderId) setPartnerError(false)
  }, [values.selectedProviderId])

  // In edit mode, allow back navigation if time-related fields are editable
  // This allows users to go back to SelectTime but not to SelectLocations
  const isTimeEditable = values.editableFields?.includes('pickUpTimeStamp')

  // Check if location actually supports flight information
  const hasFlightInfoTag = values.departure?.locationInformation?.some(
    info => info.tag === 'FLIGHT_NBR'
  )

  // Flight fields are only truly editable if the location supports flight info
  const isFlightNumberEditable = hasFlightInfoTag && values.editableFields?.includes('originLocation.flightInfo.flightNumber')
  const isCheckedBaggageEditable = hasFlightInfoTag && values.editableFields?.includes('originLocation.flightInfo.checkedInBaggage')
  const allowBackInEditMode = inEditMode && (isTimeEditable || isFlightNumberEditable || isCheckedBaggageEditable)

  if (inEditMode) {
    console.log('=== SelectHow edit mode back button logic ===')
    console.log('editableFields:', values.editableFields)
    console.log('hasFlightInfoTag:', hasFlightInfoTag)
    console.log('isTimeEditable:', isTimeEditable)
    console.log('isFlightNumberEditable:', isFlightNumberEditable)
    console.log('isCheckedBaggageEditable:', isCheckedBaggageEditable)
    console.log('allowBackInEditMode:', allowBackInEditMode)
  }

  const buttons = inEditMode && !allowBackInEditMode
    ? [{ func: wrappedNext, title: t('button-continue') }]
    : [
        { func: prev, isBack: true },
        { func: wrappedNext, title: t('button-continue') },
      ]

  useEffect(() => {
    // Handle vehicle options from SAS here
    // In edit mode, only unset options if they're not currently selected
    if (!petAvailable && !(inEditMode && petCurrentlySelected)) {
      setFieldValue('pet', false, false)
    }
    if (!lowAvailable && !(inEditMode && lowCurrentlySelected)) {
      setFieldValue('low', false, false)
    }
    if (!stationWagonAvailable) {
      setFieldValue('stationWagon', false, false)
    }
    if (numSeatsMax === 0 && !(inEditMode && boosterCurrentlySelected)) {
      setFieldValue('booster', 0, false)
    }
    else if (numSeatsMax > 0 && values.booster > numSeatsMax) {
      setFieldValue('booster', numSeatsMax, false)
    }
  }, [petAvailable, lowAvailable, stationWagonAvailable, numSeatsMax, values.booster, setFieldValue, inEditMode, petCurrentlySelected, lowCurrentlySelected, boosterCurrentlySelected])

  useEffect(() => {
    const newCompanyValue = company !== false ? company : false
    const newIsCompanyTrip = company !== false

    // Only update if values have actually changed
    if (values.company !== newCompanyValue || values.isCompanyTrip !== newIsCompanyTrip) {
      setFieldValue('company', newCompanyValue, false)
      setIsCompanyTrip(newIsCompanyTrip)
    }
  }, [company, values.company, values.isCompanyTrip, setFieldValue, setIsCompanyTrip])

  useEffect(() => {
    if (carTypeRef.current) carTypeRef.current.focus()
  }, [carTypeRef])

  // Auto-switch service type if the selected one is disabled by passenger count
  useEffect(() => {
    if (!values.passengerCount || inEditMode) return
    const currentField = fields.find(f => f.value === values.serviceType)
    if (currentField?.disabled) {
      const firstFit = fields.find(f => !f.disabled)
      if (firstFit) setFieldValue('serviceType', firstFit.value, false)
    }
  }, [values.passengerCount, fields])

  // Auto-select the first available provider:
  // - Always select the first if none is selected
  // - Keep current selection if still available after service type / add-on change
  // - Fall back to first if current selection is no longer available
  useEffect(() => {
    if (!shouldShowProviderSelection || !multipleProviders?.length) {
      if (values.selectedProviderId) setFieldValue('selectedProviderId', '', false)
      return
    }
    const currentStillAvailable = multipleProviders.some(p => p.providerData.providerId === values.selectedProviderId)
    if (!currentStillAvailable) {
      setFieldValue('selectedProviderId', multipleProviders[0].providerData.providerId, false)
    }
  }, [multipleProviders, shouldShowProviderSelection])
  return (
    <div className={cx(common.widgetWrapper, {fullscreen:fullscreen})}>
      <FormContent>
        <FormHeader onlyFullscreen>{inEditMode ? t('edit-your-trip') : t('your-trip')}</FormHeader>
        <FormHeader onlyMobile>{t('header-how-do-you-want-to-go')}</FormHeader>
        <Notifications fullscreen={fullscreen}/>
        <BookingSummary values={values} showHow currentView={currentView} />
      </FormContent>
      <div className={cx(common.contentBottom, showCompany && !inEditMode ? 'app-lg:!pb-36' : 'app-lg:!pb-20')}>
        <EditFieldsExpirationWarning />
        <FormHeader onlyFullscreen>{t('header-how-do-you-want-to-go')}</FormHeader>
        {partnerData && <Notifications fullscreen={fullscreen} forceMessages={[CONFIG.svea_module ? 'Svea Taxi Allians består av ett nätverk av partners. Tillgängliga tjänster kan variera ' : 'Du reser med en av våra Svea Partner. Tillgängliga tjänster kan variera.']} skipReadMore={true}/> }
        <MotionElement.div className={common.group}>
          <Field
            field={{
              name: 'fixedPrice',
              onChange: handleChange,
              onBlur: handleBlur,
              value: fixedPrice
            }}
            tabIndex="0"
            title={t('title-fixed-price')}
            disabled={disableFixedPrice || fixedPriceNotAvailable || taxameterNotAvailable || inEditMode}
            component={FixedPriceSwitch}
          >
            {inEditMode && <p className={common.subtitle}>Kan inte ändras vid redigering</p>}
            {disableFixedPrice && !inEditMode && <p className={common.subtitle}>Fastpris kräver destination</p>}
            {fixedPriceNotAvailable && !disableFixedPrice && !inEditMode && <p className={common.subtitle}>{t('info-fixed-price-not-available')}</p>}
            {taxameterNotAvailable && !disableFixedPrice && !fixedPriceNotAvailable && !inEditMode && <p className={common.subtitle}>{t('info-taxameter-not-available')}</p>}
            <ErrorMessage name="fixedPrice" component={FormError} noMarginTop />
          </Field>
          {shouldShowProviderSelection && <div className="mt-4"><SectionHeading number={1} title={t('header-select-car')} /></div>}
          {fields.map((field, index) => <Field key={index} type="radio" name="serviceType" component={CarRadioField} {...field} />)}
          <Field 
            name="electric" 
            title={t('title-electric-car')} 
            info={t('info-electric-car')}
            disabled={!electricCarAvailable}
            component={ElectricCarCheckbox}
          />
        </MotionElement.div>
        <MotionElement.div className={common.group}>
          {/* Negative margin bleeds the card to the contentBottom edges so inner
              items (px-2) align horizontally with the regular content rows */}
          <div className="-mx-2">
            <VehicleOptionsRenderer
              vehicleOptions={vehicleOptions}
              editableVehicleOptions={editableVehicleOptions}
              values={values}
              inEditMode={inEditMode}
            />
          </div>
        </MotionElement.div>
        {shouldShowProviderSelection && (
          <div className="mt-6">
            <PartnerSelection providers={multipleProviders} sectionNumber={2} error={partnerError} headingRef={partnerHeadingRef} />
          </div>
        )}
      </div>

      <ButtonDrawer buttons={buttons} fullscreen>
        {showCompany && !inEditMode && (
          <Field
            icon1='Private'
            icon2='Company'
            name="isCompanyTrip"
            tabIndex="0"
            title={t('title-private-or-company')}
            info={company?.name || t('info-private')}
            noPadding
            component={TripTypeSwitch}
          />
        )}
      </ButtonDrawer>
    </div>
  )
}

SelectHow.validationSchema = null

export default SelectHow
